import React, { useEffect, useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';

import { colors } from '../theme';
import ConsentChip from './ConsentChip';
import { matchesSearch } from '../../../components/searchMatch';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 440,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 1, 1, 2.5),
  },
  title: {
    flexGrow: 1,
    fontSize: 17,
    fontWeight: 600,
    color: colors.text,
  },
  tabs: {
    padding: theme.spacing(0, 1.5),
    borderBottom: `1px solid ${colors.border}`,
  },
  search: {
    margin: theme.spacing(1.5, 2.5),
  },
  list: {
    flexGrow: 1,
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.1, 2.5),
    borderBottom: `1px solid ${colors.border}`,
  },
  who: {
    flexGrow: 1,
    minWidth: 0,
    marginRight: theme.spacing(1),
  },
  company: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.text,
  },
  details: {
    fontSize: 12,
    color: colors.textDim,
  },
  empty: {
    padding: theme.spacing(3, 2.5),
    color: colors.textDim,
    fontSize: 13,
  },
  foot: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5, 2.5),
    borderTop: `1px solid ${colors.border}`,
    '& > *': { marginLeft: theme.spacing(1) },
  },
}));

const matches = (contact, query) =>
  matchesSearch(query, [contact.company, contact.name, contact.region], [contact.phone]);

// Кто в списке рассылки: панель справа с двумя группами — кто получит сообщение и кто нет.
// Открывается с плашек под полем «Список получателей» сразу на нужной группе.
function AudienceDrawer({
  open,
  onClose,
  group,
  listName,
  going,
  skipped,
  goingLabel,
  skippedLabel,
  onEditList,
  onCollectConsent,
}) {
  const classes = useStyles();
  const [tab, setTab] = useState(group);
  const [search, setSearch] = useState('');

  // Каждое открытие — с той группы, по которой нажали, и без старого поиска.
  useEffect(() => {
    if (open) {
      setTab(group);
      setSearch('');
    }
  }, [open, group]);

  const contacts = tab === 'skipped' ? skipped : going;
  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? contacts.filter((contact) => matches(contact, query)) : contacts;
  }, [contacts, search]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.head}>
        <div className={classes.title}>{`Список «${listName}»`}</div>
        <IconButton onClick={onClose} aria-label="Закрыть">
          <CloseIcon />
        </IconButton>
      </div>
      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab value="going" label={`${goingLabel} (${going.length})`} />
        {skipped.length > 0 && <Tab value="skipped" label={`${skippedLabel} (${skipped.length})`} />}
      </Tabs>
      <TextField
        className={classes.search}
        variant="outlined"
        size="small"
        placeholder="Компания, директор или телефон"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <ul className={classes.list}>
        {shown.map((contact) => (
          <li key={contact.id} className={classes.row}>
            <div className={classes.who}>
              <div className={classes.company}>{contact.company || contact.name || contact.phone}</div>
              <div className={classes.details}>
                {[contact.company ? contact.name : '', contact.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
            <ConsentChip status={contact.consentStatus} />
          </li>
        ))}
        {shown.length === 0 && (
          <li className={classes.empty}>{search.trim() ? 'Никого не нашлось' : 'Здесь никого нет'}</li>
        )}
      </ul>
      <div className={classes.foot}>
        {tab === 'skipped' && onCollectConsent && (
          <Button color="primary" onClick={onCollectConsent}>
            Собрать согласие
          </Button>
        )}
        <Button variant="outlined" color="primary" onClick={onEditList}>
          Изменить список
        </Button>
      </div>
    </Drawer>
  );
}

export default AudienceDrawer;
